/**
 * Root shell: wraps every authenticated and public route with Header, FeedBar (home only),
 * NavDrawer, BottomNav, FloatingPostButton, and the scrollable main Outlet.
 * PostFormProvider is mounted here so a single PostForm instance is shared across the whole app.
 */
import { useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Header } from './Header'
import { FeedBar } from './FeedBar'
import { BottomNav } from './BottomNav'
import { FloatingPostButton } from './FloatingPostButton'
import { NavDrawer } from './NavDrawer'
import { PostFormProvider, usePostForm } from '@/context/PostFormContext'
import { PostForm } from '@/components/post/PostForm'
import { useAuthStore } from '@/store/auth.store'
import { InstallBanner } from '@/components/InstallBanner'
import { UpdateAvailableBanner } from '@/components/UpdateAvailableBanner'

function LayoutInner() {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const location = useLocation()
  const { isAuthenticated } = useAuthStore()
  const { open, initialPost, initialTab, groupContext, close } = usePostForm()
  const isHome = location.pathname === '/'
  // Nível 1 (FeedBar) is fixed chrome for all top-level tab destinations
  // named in ROADMAP_FASE_DESCOBERTA.md §1 — Local/Following (home), Groups
  // (list only, not an individual /groups/:slug page — that's a drill-down,
  // not a tab), Search — "nenhuma exceção por tela". Originally this only
  // covered Home+Map (spec-map-ajuste-nav.md); extended to Groups/Search
  // after manual testing showed the same gap on those tabs (Nível 1
  // vanishing when the Groups nav button was tapped); Map itself was later
  // merged into Search (spec-search-map-merge.md), dropping its own entry
  // here. InstallBanner stays a separate, Home-only concern, kept off
  // showFeedBar's back.
  const showFeedBar = isHome || ['/search', '/groups'].includes(location.pathname)
  const isUnauthHome = !isAuthenticated && isHome

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        background: 'var(--bg)',
        position: 'relative',
        isolation: 'isolate',
        overflow: 'hidden',
      }}
    >
      {/* Spectrum bar — desktop only, spans full viewport top */}
      <div
        className="oh-spectrum hidden lg:block"
        style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 5, borderRadius: 0, zIndex: 90, flexShrink: 0 }}
      />

      {/* Full-viewport welcome gradient — desktop only */}
      {isUnauthHome && (
        <div
          className="hidden lg:block"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 320,
            background: 'linear-gradient(180deg, color-mix(in srgb, var(--brand) 17%, var(--bg)) 0%, transparent 100%)',
            zIndex: -1,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* On desktop: sidebar + feed are grouped and centered together as a unit.
          On mobile: single column, NavDrawer is fixed (out of flow). */}
      <div className="flex-1 flex flex-col lg:flex-row lg:justify-center lg:pt-[5px] overflow-hidden min-w-0">
        <NavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

        {/* Feed column: full width on mobile, fixed 680px on desktop */}
        <div className="relative flex-1 lg:flex-none lg:w-[680px] flex flex-col overflow-hidden min-w-0">
          <Header onMenuOpen={() => setDrawerOpen(true)} />
          <UpdateAvailableBanner />
          {isAuthenticated && isHome && <InstallBanner />}
          {showFeedBar && <FeedBar />}

          <main
            id="feed-scroll"
            className={isUnauthHome ? 'lg:!bg-transparent' : ''}
            style={{ flex: 1, overflowY: 'auto', background: 'var(--bg-sunk)' }}
          >
            <Outlet />
          </main>

          <FloatingPostButton />
          <BottomNav />
        </div>
      </div>

      <PostForm open={open} onClose={close} initialPost={initialPost} initialTab={initialTab} groupContext={groupContext} />
    </div>
  )
}

export function Layout() {
  return (
    <PostFormProvider>
      <LayoutInner />
    </PostFormProvider>
  )
}
